import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import papa from 'papaparse'
import {
  useEvent,
  getFormattedDateAndTime,
  formatDOB,
  getFormattedStartAndEndTimeWithFormat,
  getFormattedAddress,
  useEventAttendees,
  useLazyEventAttendees,
  useStateWithDebounce,
  SortDirectionEnum,
  TestTypeLabels,
  PaymentMethod,
  EventAttendees_eventAttendees_attendees_Payment,
  EventAttendees_eventAttendees_attendees,
  PROVIDER,
  useAuth,
  UserPermissions,
  SortByType,
  Event,
  TestType,
  useSublocation,
  getSubscriberName,
} from '@modmd/data'
import { Box } from 'components/Layout'
import Card from 'components/Card'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { COLOR, DEVICE } from 'theme'
import { Text } from 'components/Typography'
import Label from 'components/Label'
import Datalist from 'components/Datalist'
import TextInput from 'components/TextInput'
import Pagination from 'components/Pagination'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DownloadTestResultPdfEventButton } from 'sharedComponents/DownloadTestResultPdfButton'
import { SelectInput } from 'components/SelectInput'
import { hoursToMinutes } from 'date-fns'
import { getResultLabelColor, isInfluenza } from 'utils/helpers'
import { QRCodeModal } from 'sharedComponents/QRCodeModal/QRCodeModal'
import { LAB_OPTIONS } from 'internal-portal/routes/TestSessions/utils'
import { AttendeeInfo } from './AttendeeInfo'

interface RouterProps {
  eventId: string
  isClient?: boolean
  setIsLoadingEventAttendee?: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoadingEventData?: React.Dispatch<React.SetStateAction<boolean>>
  setEventData?: React.Dispatch<
    React.SetStateAction<{
      data: Event
      refetch: () => void
      loading: boolean
    }>
  >
  setHasPermission?: React.Dispatch<React.SetStateAction<boolean>>
}

export enum UrlSearchQuery {
  EMAIL_OR_NAME_LIKE = 'emailOrNameLike',
  PAGE = 'page',
  ORDER_BY = 'orderBy',
  DIRECTION = 'direction',
  EVENT_DAY_ID = 'eventDayId',
}

const PERPAGE = 10

const EventComponent: React.VFC<RouterProps> = ({
  eventId,
  isClient,
  setIsLoadingEventData,
  setIsLoadingEventAttendee,
  setEventData,
  setHasPermission,
}) => {
  const history = useHistory()
  const { hasPermission } = useAuth()
  const [downloadCSV, setDownloadCSV] = useState(false)
  const [downloadInsuranceCSV, setDownloadInsuranceCSV] = useState(false)
  const [QRCodeDialog, setQRCodeDialog] = useState(false)
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isAttendeeInfoOpen, setIsAttendeeInfoOpen] = useState<string>('')
  const searchState = useSearchState<{
    [UrlSearchQuery.EMAIL_OR_NAME_LIKE]?: string
    [UrlSearchQuery.PAGE]?: string
    [UrlSearchQuery.ORDER_BY]?: string
    [UrlSearchQuery.DIRECTION]?: SortDirectionEnum
    [UrlSearchQuery.EVENT_DAY_ID]?: string
  }>()
  if (setHasPermission) {
    setHasPermission(hasPermission([UserPermissions.CLOSE_EVENT]))
  }
  const { data: sublocations } = useSublocation()

  const [emailOrNameLike, setEmailOrNameLike, debouncedSearchQuery] = useStateWithDebounce(
    searchState.data.emailOrNameLike,
    (emailOrNameLike) => {
      searchState.setSearchState(
        { emailOrNameLike, page: emailOrNameLike ? 1 : searchState.data.page },
        false,
        true
      )
    },
    1000
  )

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      searchState.setSearchState({ ...searchState.data, [name]: value, page: 1 })
    },
    [searchState]
  )

  const data = useEvent({
    id: eventId,
  })
  const { data: eventData, refetch, loading: isLoadingEventData } = data

  React.useEffect(() => {
    if (setIsLoadingEventData) setIsLoadingEventData(isLoadingEventData)
    if (setEventData && eventData)
      setEventData({ data: eventData, loading: isLoadingEventData, refetch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData])

  const formatToInsuraanceCSV = ({
    firstName,
    lastName,
    dateOfBirth,
    Payment,
    gender,
    Address,
    EventSamples,
  }: EventAttendees_eventAttendees_attendees) => {
    const subscriberName = getSubscriberName(
      Payment?.[0]?.subscriberFirstName,
      Payment?.[0]?.subscriberMiddleName,
      Payment?.[0]?.subscriberLastName,
      ''
    )
    return {
      'First Name': firstName || '',
      'Last Name': lastName || '',
      'Date of Birth': formatDOB(dateOfBirth),
      SSN: Payment?.[0]?.SSN,
      Gender: gender,
      'Insured / Uninsured': !!(
        (Payment?.[0]?.insuranceCardFrontChecked &&
          Payment?.[0]?.insuranceCardBackChecked &&
          Payment?.[0]?.govIdChecked) ||
        Payment?.[0]?.insuranceCompany
      ),
      'Insurance Name': Payment?.[0]?.insuranceCompany,
      'Insurance ID number': Payment?.[0]?.cardID,
      'Group Number (optional)': Payment?.[0]?.groupNumber,
      'Patient is subscriber or not': subscriberName,
      DOS: EventSamples && getFormattedDateAndTime(EventSamples[0]?.createdAt),
      'Address line': Address?.street,
      City: Address?.city,
      State: Address?.state,
      Zip: Address?.zip,
      'Rendering provider name': PROVIDER.name,
      'Rendering provider NPI': PROVIDER.npi,
      'Reffering Provider name': PROVIDER.name,
      'Reffering Provider NPI': PROVIDER.npi,
      'Test performed': eventData?.event?.testType || '',
      'Travel (yes/no)': '',
      'Insurance card copy collected (yes/no)':
        !!Payment?.[0]?.insuranceCardFront && !!Payment?.[0]?.insuranceCardBack,
      'Driving license copy collected (yes/no)': !!Payment?.[0]?.driversLicense,
      'Service rendered location': 'ModmdLab',
    }
  }

  const formatToNormalCSV = ({
    firstName,
    lastName,
    dateOfBirth,
    EventSamples,
  }: EventAttendees_eventAttendees_attendees) => ({
    'First name': firstName || '',
    'Last name': lastName || '',
    'Date of Birth': formatDOB(dateOfBirth),
    eventName: eventData?.event?.name || '',
    type: eventData?.event?.testType || '',
    result: EventSamples?.[0]?.result,
  })

  const [getEventAttendees, { loading: loadingDownloadCSV }] = useLazyEventAttendees({
    onCompleted: ({ eventAttendees }) => {
      if (downloadCSV) {
        const csvData = downloadInsuranceCSV
          ? eventAttendees?.attendees
              ?.filter((data) => data.EventSamples?.length)
              .map((data) => formatToInsuraanceCSV(data)) || []
          : eventAttendees?.attendees?.map((data) => formatToNormalCSV(data)) || []
        const csv = papa.unparse(csvData, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Event-${eventData?.event?.id || ''}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownloadCSV(false)
        setDownloadInsuranceCSV(false)
      }
    },
  })

  const {
    data: eventAttendeeData,
    loading: isLoadingEventAttendee,
    refetch: refetchAttendeesData,
  } = useEventAttendees({
    eventId: hasPermission([UserPermissions.SEE_EVENT_ATTENDEE]) ? eventId : '',
    page: Number(searchState.data.page ?? 1),
    eventDayId: searchState.data.eventDayId,
    emailOrNameLike: debouncedSearchQuery,
    pageLength: PERPAGE,
    sortBy: searchState.data.orderBy,
    direction: searchState.data.direction,
  })

  React.useEffect(() => {
    if (setIsLoadingEventAttendee) setIsLoadingEventAttendee(isLoadingEventAttendee)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData])

  const attendees = useMemo(() => eventAttendeeData?.eventAttendees?.attendees || null, [
    eventAttendeeData?.eventAttendees?.attendees,
  ])

  const pagination = useMemo(() => eventAttendeeData?.eventAttendees?.pagination || null, [
    eventAttendeeData?.eventAttendees?.pagination,
  ])

  if (!eventData?.event && !isLoadingEventData) {
    history.goBack()
    return null
  }

  const handleDownloadCSV = () => {
    setDownloadCSV(true)

    void getEventAttendees({
      variables: {
        id: eventId,
        filterData: {
          ...(debouncedSearchQuery && {
            emailOrNameLike: debouncedSearchQuery,
          }),
          ...(searchState.data.eventDayId && {
            eventDayId: searchState.data.eventDayId,
          }),
        },
        pagination: {
          from: 0,
          length: pagination?.total || 100,
        },
      },
    })
  }

  const handleDownloadInsuranceCSV = () => {
    setDownloadCSV(true)
    setDownloadInsuranceCSV(true)

    void getEventAttendees({
      variables: {
        id: eventId,
        filterData: {
          ...(debouncedSearchQuery && {
            emailOrNameLike: debouncedSearchQuery,
          }),
          ...(searchState.data.eventDayId && {
            eventDayId: searchState.data.eventDayId,
          }),
        },
        pagination: {
          from: 0,
          length: pagination?.total || 100,
        },
      },
    })
  }

  const paymentMethodLabel = (
    paymentMethod: PaymentMethod | null | undefined,
    Payment: Array<EventAttendees_eventAttendees_attendees_Payment | null> | null | undefined
  ) => {
    if (paymentMethod === PaymentMethod.Invoice) {
      return <Label indicator="blue">Invoicing</Label>
    }
    if (paymentMethod === PaymentMethod.Insurance) {
      return <Label indicator="zimaBlue">Insurance</Label>
    }
    if (paymentMethod === PaymentMethod.CreditCard) {
      if (Payment?.length && Payment[0]?.paymentError) return <Label indicator="red">Fail</Label>
      if (Payment?.length && !Payment[0]?.paymentError) return <Label indicator="green">Paid</Label>
      return <Label indicator="yellow">On Hold</Label>
    }
    return <Label indicator="blue">Invoicing</Label>
  }

  return (
    <>
      <Card>
        <Card.Title
          rightContent={
            <>
              {!eventData?.event?.finishedAt && (
                <Label size="l" indicator={eventData?.event?.isActive ? 'green' : 'red'}>
                  {eventData?.event?.isActive ? 'Active' : 'Inactive'}
                </Label>
              )}
              {eventData?.event?.finishedAt &&
                (eventData?.event?.Invoice?.invoiceDocNumber ? (
                  <Label size="l" indicator="lightGreen">
                    Invoiced NO: {eventData?.event?.Invoice?.invoiceDocNumber}
                  </Label>
                ) : (
                  <Label size="l" indicator="red">
                    Finished
                  </Label>
                ))}
              <Button
                size="small"
                appearance="link"
                onClick={() => setQRCodeDialog(true)}
                leftIcon={<Icon.ExternalLinkOutline />}
              >
                QRCode
              </Button>
            </>
          }
        >
          Details
        </Card.Title>
        <Card.Content>
          <Box display="grid" gridGap="0.25rem">
            <Box display="flex">
              <Icon.Calendar flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
              <Box display="flex" alignItems="center">
                <Text fontSize="m">
                  {!!eventData?.event?.name && eventData?.event.name} -{' '}
                  {!!eventData?.event?.id && eventData?.event.id}
                </Text>
              </Box>
            </Box>
            <Box display="flex">
              <Icon.Calendar flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
              <Box>
                <Text fontSize="m">
                  {eventData?.event?.frequency}{' '}
                  {eventData?.event?.frequency === 'Weekly' && (
                    <Text fontSize="m" color={COLOR.grayDark}>
                      ({eventData?.event?.daysArray?.join(', ')})
                    </Text>
                  )}
                </Text>
                <Box display="flex" flexDirection="column" paddingLeft="1rem">
                  {eventData?.event?.EventDays?.map((eventDay, index) => (
                    <Box>
                      {eventData?.event?.frequency === 'Once' &&
                        getFormattedDateAndTime(
                          eventDay.startTime,
                          false,
                          true,
                          'MM/dd/yyyy (cccc)'
                        )}{' '}
                      {eventData?.event?.frequency === 'Once' && (
                        <Text fontSize="m" color={COLOR.grayDark}>
                          {getFormattedStartAndEndTimeWithFormat(
                            new Date(eventDay.startTime),
                            hoursToMinutes(eventDay.timeRange),
                            'hh:mmaa'
                          )}
                        </Text>
                      )}
                      {eventData?.event?.frequency === 'Weekly' && index === 0 && (
                        <Text fontSize="m" color={COLOR.grayDark}>
                          {getFormattedStartAndEndTimeWithFormat(
                            new Date(eventDay.startTime),
                            hoursToMinutes(eventDay.timeRange),
                            'hh:mmaa'
                          )}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            {process.env.REACT_APP_SESSION_PHYSICIAN_NAME && (
              <Box display="flex" alignItems="center">
                <Icon.Heartbeat flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">
                  Physician name: {process.env.REACT_APP_SESSION_PHYSICIAN_NAME}
                </Text>
              </Box>
            )}
            {process.env.REACT_APP_SESSION_LAB_ADDRESS && (
              <Box display="flex" alignItems="center">
                <Icon.ThermometerOutline flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">Lab address: {process.env.REACT_APP_SESSION_LAB_ADDRESS}</Text>
              </Box>
            )}
            {/* // TODO: add after adding lab column in events */}
            {/* <Loading isEmpty={false}>
              <Box display="flex" alignItems="center">
                <Box flex="none" mr="0.5rem">
                  <Icon.ThermometerOutline flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                </Box>
                <SelectInput
                  value={eventData?.event?.testType ?? NO_LAB_VALUE}
                  name="Lab"
                  disabled
                  options={
                    eventData?.event?.testType === TestType.Covid19
                      ? [{ value: NO_LAB_VALUE, label: 'Select Lab' }, ...LAB_OPTIONS]
                      : [
                          { value: NO_LAB_VALUE, label: 'Select Lab' },
                          LAB_OPTIONS[1],
                          LAB_OPTIONS[2],
                        ]
                  }
                />
              </Box>
            </Loading> */}
            <Box display="flex" alignItems="center">
              <Icon.Pin flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
              <Text fontSize="m">
                {eventData?.event?.Address
                  ? getFormattedAddress(eventData.event.Address)
                  : 'No address'}
              </Text>
            </Box>

            {eventData?.event?.testType && (
              <Box display="flex" alignItems="center">
                <Icon.Heartbeat flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">
                  Test type:{' '}
                  {TestTypeLabels[eventData.event.testType as keyof typeof TestTypeLabels]}
                </Text>
              </Box>
            )}
            {eventData?.event?.sublocation && (
              <Box display="flex" alignItems="center">
                <Icon.Home flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">
                  Rate type:{' '}
                  {
                    sublocations?.sublocations.filter(
                      (sub) => sub.name === eventData.event?.sublocation
                    )[0].displayName
                  }
                </Text>
              </Box>
            )}
            {eventData?.event?.lab && (
              <Box display="flex" alignItems="center">
                <Icon.Test flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">
                  Lab: {LAB_OPTIONS.filter((lab) => lab.value === eventData.event?.lab)[0].label}
                </Text>
              </Box>
            )}
            {eventData?.event?.paymentMethod === PaymentMethod.CreditCard && (
              <Box display="flex" alignItems="center">
                <Icon.CreditCardOutline flex="none" size="XL" color={COLOR.gray} mr="0.5rem" />
                <Text fontSize="m">Unitary Price: $ {eventData?.event?.price} </Text>
              </Box>
            )}
          </Box>
        </Card.Content>
      </Card>
      <Box mt="1rem">
        <Card>
          <Card.Title
            rightContent={
              <>
                <Button
                  href={`${process.env.REACT_APP_MOD_EVENT_URL as string}/event/${
                    eventData?.event?.id as string
                  }/event-days`}
                  appearance="link"
                >
                  Go to Event
                </Button>
              </>
            }
          >
            Event Days
          </Card.Title>
          <Card.Content>
            <Box>
              {eventData?.event?.EventDays?.map((eventDay) => (
                <Card key={eventDay.id} mb="1rem">
                  <Card.Title>COD.: {eventDay.id}</Card.Title>
                  <Card.Content>
                    <Box display="flex" justifyContent="space-between">
                      <Box display="flex" flexDirection="column">
                        <Text fontWeight="bold">
                          {getFormattedDateAndTime(
                            eventDay.startTime,
                            false,
                            true,
                            'LLLL do (EEEE)'
                          )}
                        </Text>
                        <Text color={COLOR.grayDark}>
                          {getFormattedStartAndEndTimeWithFormat(
                            new Date(eventDay.startTime),
                            hoursToMinutes(eventDay.timeRange),
                            'hh:mmaa'
                          )}
                        </Text>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Text fontWeight="bold">
                          {`${Number(eventDay?.remainingTests)} remaining tests`}
                        </Text>
                      </Box>
                    </Box>
                  </Card.Content>
                </Card>
              ))}
            </Box>
          </Card.Content>
        </Card>
        {hasPermission([UserPermissions.SEE_EVENT_ATTENDEE]) && (
          <Box mt="1rem">
            <Card flex="3" width="100%" minWidth="280px" p="0">
              <Card.Title
                rightContent={
                  hasPermission([UserPermissions.DOWNLOAD_EVENT_ATTENDEE_TEST_CSV]) && (
                    <>
                      <Button
                        onClick={handleDownloadCSV}
                        isFetching={loadingDownloadCSV}
                        disabled={!attendees?.length}
                      >
                        Download CSV
                      </Button>
                      {eventData?.event?.paymentMethod === PaymentMethod.Insurance && (
                        <Button
                          onClick={handleDownloadInsuranceCSV}
                          isFetching={loadingDownloadCSV}
                          disabled={!attendees?.length}
                        >
                          Download Insurance CSV
                        </Button>
                      )}
                    </>
                  )
                }
              >
                <Box alignContent="flex-start" gridGap="1rem" display="flex">
                  {hasPermission([UserPermissions.SEARCH_EVENT_ATTENDEE]) && (
                    <TextInput
                      placeholder="Search by User code or Name"
                      rightIcon={<Icon.Search color={COLOR.grayDark} size="L" />}
                      value={emailOrNameLike}
                      onChange={(e) => setEmailOrNameLike(e.target.value)}
                      width="50%"
                    />
                  )}
                  {eventData?.event?.EventDays &&
                    hasPermission([UserPermissions.SEE_EVENT_ATTENDEE_BY_DAY]) && (
                      <SelectInput
                        width="50%"
                        name={UrlSearchQuery.EVENT_DAY_ID}
                        label="Event Day"
                        value={searchState.data.eventDayId || ''}
                        onChange={handleInputChange}
                        options={[
                          { value: '', label: 'All' },
                          ...eventData.event.EventDays.map((eventDay) => ({
                            value: eventDay.id,
                            label: `${eventDay.id} (${getFormattedStartAndEndTimeWithFormat(
                              new Date(eventDay.startTime),
                              hoursToMinutes(eventDay.timeRange),
                              'hh:mmaa'
                            )})`,
                          })),
                        ]}
                      />
                    )}
                </Box>
              </Card.Title>

              <Datalist
                isLoading={isLoadingEventAttendee}
                data={attendees || []}
                sorter={{
                  direction: searchState.data.direction,
                  sortBy: searchState.data.orderBy,
                }}
                onSortChange={(orderBy, direction) => {
                  searchState.setSearchState({ direction, orderBy }, false)
                }}
                shouldRenderTableHeader={isDesktop}
                showActionIndicators
                renderTableHeader={() => (
                  <Datalist.Row display="grid" gridTemplateColumns="repeat(8, 1fr)">
                    <Datalist.HeaderCell label="ID" />
                    <Datalist.HeaderCell label="Test" />
                    <Datalist.HeaderCell label="Payment" />
                    <Datalist.HeaderCell label="Patient" sortKey={SortByType.FIRST_NAME} />
                    <Datalist.HeaderCell label="Barcode" />
                    <Datalist.HeaderCell
                      label="Date"
                      sortKey={SortByType.EVENT_SAMPLE_CREATED_AT}
                    />
                    <Datalist.HeaderCell label="Result" sortKey={SortByType.EVENT_SAMPLE_RESULT} />
                  </Datalist.Row>
                )}
                renderTableRow={({ id, firstName, EventSamples, lastName, Payment }) => (
                  <Datalist.Row
                    gridTemplateColumns={{
                      _: 'repeat(2, 1fr)',
                      TABLET: 'repeat(3, 1fr)',
                      DESKTOP: `repeat(8, 1fr) 2rem`,
                    }}
                    onClick={() => setIsAttendeeInfoOpen(id)}
                  >
                    <Datalist.Cell label="ID">{id}</Datalist.Cell>
                    <Datalist.Cell label="Test">{eventData?.event?.testType ?? '-'}</Datalist.Cell>

                    <Datalist.Cell label="Payment">
                      {paymentMethodLabel(eventData?.event?.paymentMethod, Payment)}
                    </Datalist.Cell>
                    <Datalist.Cell label="Patient">
                      {firstName} {lastName}
                    </Datalist.Cell>
                    <Datalist.Cell label="Barcode">
                      {EventSamples?.length ? (
                        <Label indicator="green">{EventSamples[0]?.barcode || ''}</Label>
                      ) : (
                        <Label indicator="red">Not tested yet</Label>
                      )}
                    </Datalist.Cell>
                    <Datalist.Cell label="Date">
                      {EventSamples && getFormattedDateAndTime(EventSamples[0]?.createdAt)}
                    </Datalist.Cell>
                    <Datalist.Cell label="Result">
                      {EventSamples && EventSamples[0]?.result ? (
                        <>
                          <Label
                            indicator={
                              EventSamples[0]?.result
                                ? getResultLabelColor(EventSamples[0]?.result)
                                : 'blue'
                            }
                          >
                            {isInfluenza(eventData?.event?.testType) && `A: `}
                            {EventSamples[0]?.result || 'Pending result'}
                          </Label>
                          {isInfluenza(eventData?.event?.testType as TestType) &&
                            EventSamples[0]?.secondResult && (
                              <Label indicator={getResultLabelColor(EventSamples[0]?.secondResult)}>
                                {`B: ${EventSamples[0]?.secondResult}`}
                              </Label>
                            )}
                        </>
                      ) : (
                        '-'
                      )}
                    </Datalist.Cell>
                    <Datalist.Cell>
                      {EventSamples && EventSamples[0]?.id && (
                        <DownloadTestResultPdfEventButton
                          appearance="link"
                          size="large"
                          testId={EventSamples[0]?.id}
                          testType={eventData?.event?.testType || ''}
                          isDisabled={!EventSamples[0]?.result}
                        />
                      )}
                    </Datalist.Cell>
                  </Datalist.Row>
                )}
              />
            </Card>
            {pagination && pagination?.total > pagination?.length && (
              <Pagination.Wrapper>
                <Pagination
                  firstPage={1}
                  currentPage={Number(searchState.data.page ?? 1)}
                  lastPage={Math.ceil(pagination.total / pagination.length)}
                  onPageChange={(page) => {
                    searchState.setSearchState({ page }, false)
                  }}
                />
              </Pagination.Wrapper>
            )}
          </Box>
        )}
      </Box>

      <AttendeeInfo
        id={isAttendeeInfoOpen}
        companyId={eventData?.event?.companyId || ''}
        isOpen={!!isAttendeeInfoOpen}
        handleDismiss={() => setIsAttendeeInfoOpen('')}
        refetchAttendeesData={refetchAttendeesData}
        isClient={isClient}
      />
      <QRCodeModal
        isOpen={QRCodeDialog}
        onDismiss={() => setQRCodeDialog(false)}
        title={`${eventData?.event?.name ?? ''} - ${eventData?.event?.id ?? ''}`}
        subTitle="Scan the following QRCODE to proceed:"
        qrCodeValue={`${process.env.REACT_APP_MOD_EVENT_URL as string}/event/${
          eventData?.event?.id as string
        }/event-days`}
      />
    </>
  )
}

export default EventComponent
